import type { InventoryLedgerEntryWithName, LedgerReason } from '@/supabase/inventory/types/inventory';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface LedgerListProps {
  entries: InventoryLedgerEntryWithName[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  // 초기 로드 실패 상태(codexloop R1 Fix D) — InventoryTable과 동일한 이유로 optional.
  hasError?: boolean;
  onRetry?: () => void;
  // PR7: 필터 활성 중 무결과("조건에 맞는 내역이 없어요")를 진짜 빈 원장("입출고 내역이
  // 없어요")과 구분하기 위한 신호. 둘 다 optional·additive — 기존 소비처는 무변경으로 기존
  // 문구를 그대로 유지한다. 초기화 버튼은 onResetFilters가 주어졌을 때만 노출.
  isFiltered?: boolean;
  onResetFilters?: () => void;
}

// reason 칩 톤. 브리프 3-D 명시분(입고·신규등록=positive, 출고=negative, 조정=중립) +
// 주문차감·주문복원·주문변경은 입금 차감 트리거가 실제로 기록하는 값(닉네임 스냅샷 동반),
// 엑셀등록만 PR5에서 배선되는 미래분 — LedgerReason은 DB 전체 계약이라 Record 전수 커버 필수.
// 재고 증감 방향으로만 자연 확장: 엑셀등록은 입고/신규등록과 같은 증가 계열(positive),
// 주문차감은 출고와 같은 감소 계열(negative).
// 주문변경(입금 후 라인 편집 증분)·주문복원(세대 집계 반전)은 delta 부호가 양방향 —
// 방향 색은 delta 컬럼(이미 부호 기반)이 전담하고 유형 뱃지는 중립 slate.
const REASON_TONE: Record<LedgerReason, string> = {
  '입고': 'bg-green-100 text-green-700',
  '신규등록': 'bg-green-100 text-green-700',
  '엑셀등록': 'bg-green-100 text-green-700',
  '주문복원': 'bg-slate-100 text-slate-600',
  '출고': 'bg-red-100 text-red-700',
  '주문차감': 'bg-red-100 text-red-700',
  '주문변경': 'bg-slate-100 text-slate-600',
  '조정': 'bg-slate-100 text-slate-600',
};

// delta 표시 — 브리프 표기 그대로 '+N'(증가) / '−N'(감소, 유니코드 마이너스). toLocaleString의
// 로케일별 마이너스 기호 차이(하이픈 vs U+2212)에 기대지 않고 부호를 직접 붙인다.
function formatDelta(delta: number): string {
  return delta >= 0 ? `+${delta.toLocaleString('ko-KR')}` : `−${Math.abs(delta).toLocaleString('ko-KR')}`;
}

// 일시 표시 — KST 기준 'YYYY.MM.DD HH:mm'. UTC+9 시프트 후 getUTC*로 직접 추출
// (DepositRow.formatDepositDateTime 패턴 재사용, Intl locale/hour12 함정 회피).
function formatDateTimeKst(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = kst.getUTCFullYear();
  const mm = String(kst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(kst.getUTCDate()).padStart(2, '0');
  const hh = String(kst.getUTCHours()).padStart(2, '0');
  const mi = String(kst.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

export function LedgerList({ entries, isLoading, hasMore, onLoadMore, hasError, onRetry, isFiltered, onResetFilters }: LedgerListProps) {
  // 최초 로드(엔트리 0건 + 로딩 중)만 스켈레톤 — 더 보기 중엔 기존 목록을 유지한 채 버튼만 disabled.
  // reload()는 재시도 시에도 항상 isLoading을 true로 올리므로(useInventoryLedger), '다시 시도' 클릭
  // 시 아래 오류 상태 대신 이 스켈레톤이 다시 뜬다.
  if (isLoading && entries.length === 0) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-10 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (hasError && entries.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
        <p className="break-keep text-sm text-slate-500">내역을 불러오지 못했습니다</p>
        {onRetry && (
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            다시 시도
          </Button>
        )}
      </div>
    );
  }

  if (entries.length === 0) {
    // 이 분기에 도달했다는 것 자체가 !isLoading && !hasError(위 두 분기가 선점) — 분기 순서를
    // 바꾸지 않고 문구만 isFiltered로 갈라 기존 무필터 경로는 그대로 둔다(무회귀).
    if (isFiltered) {
      return (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
          <p className="break-keep text-sm text-slate-500">조건에 맞는 내역이 없습니다</p>
          {onResetFilters && (
            <Button variant="outline" size="sm" className="mt-3" onClick={onResetFilters}>
              필터 초기화
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200">
        <p className="break-keep text-sm text-slate-500">입출고 내역이 없습니다</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="divide-y divide-slate-100">
        {entries.map((entry) => {
          const itemName = entry.item_name ?? '알 수 없는 상품';
          // 수동 입출고/조정은 memo, 주문차감/주문복원/주문변경(입금 차감 트리거 행)은
          // memo가 없고 order_nickname 스냅샷이 실려온다 — 같은 슬롯에 어느 쪽이든 하나를 표시.
          const note = entry.memo ?? entry.order_nickname;
          return (
            // 모바일(<sm)은 타임스탬프를 윗줄로 스택(w-full이 줄바꿈 강제) — 인라인 슬롯
            // 축약만으론 375px 카드(내용폭 ~311px)에서 상품명이 ~59px로 붕괴(codexloop
            // 폴리시 R3). 스택 후 둘째 줄 = 이름(~155px)+칩+수량. sm+는 단일 행 불변.
            <div key={entry.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm sm:flex-nowrap">
              <span className="w-full shrink-0 tabular-nums text-xs text-slate-400 sm:w-[120px]">
                {formatDateTimeKst(entry.created_at)}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-slate-900" title={itemName}>
                {itemName}
              </span>
              {/* 칩·수량·메모는 전부 고정폭 슬롯 — 행마다 내용 유무/글자수로 컬럼 x좌표가
                  지그재그되지 않게 한다(UI 폴리시: 이전엔 메모 없는 행에서 칩·수량이 오른쪽으로
                  밀리고, 2자/4자 칩 폭 차이로 수량 시작점이 어긋났다). */}
              <span className="flex w-[76px] shrink-0 justify-center">
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REASON_TONE[entry.reason]}`}
                >
                  {entry.reason}
                </span>
              </span>
              <span
                className={`w-14 shrink-0 text-right tabular-nums font-semibold ${entry.delta >= 0 ? 'text-green-700' : 'text-red-600'}`}
              >
                {formatDelta(entry.delta)}
              </span>
              {/* note 슬롯은 sm 미만에서 숨김(codexloop 폴리시 R1) — 고정 슬롯 합이 name
                  이전에 ~470px라 375px 화면에서 페이지가 가로로 넘친다. 정렬 목적(상시 렌더)은
                  데스크톱(sm+)에서 유지, 모바일은 메모 없이 칩·수량까지만. */}
              <span
                className="hidden w-[140px] shrink-0 truncate text-xs text-slate-400 sm:block"
                title={note ?? undefined}
              >
                {note}
              </span>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div className="border-t border-slate-100 p-2.5 text-center">
          <Button variant="outline" size="sm" disabled={isLoading} onClick={onLoadMore}>
            더 보기
          </Button>
        </div>
      )}
    </div>
  );
}
